// src/features/operational/services/assessmentService.js
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch
} from "firebase/firestore";
import { db } from "../../../firebase/config";

const ATP_PLANS_COLLECTION = "annualTeachingPlans";
const ATP_ITEMS_COLLECTION = "annualTeachingPlanItems";
const POA_COLLECTION = "programmeOfAssessment";

const mapDoc = (docSnapshot) => ({
  id: docSnapshot.id,
  ...docSnapshot.data()
});

const resolveSubjectMap = async (records, subjectKey = "subject") => {
  const subjectIds = [...new Set(records.map((record) => record?.[subjectKey]).filter(Boolean))];

  if (subjectIds.length === 0) {
    return new Map();
  }

  const subjectDocs = await Promise.all(
    subjectIds.map(async (subjectId) => {
      const snapshot = await getDoc(doc(db, "subjects", subjectId));
      return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
    })
  );

  return new Map(subjectDocs.filter(Boolean).map((subject) => [subject.id, subject]));
};

const enrichWithSubjects = async (records, subjectKey = "subject") => {
  const subjectMap = await resolveSubjectMap(records, subjectKey);
  return records.map((record) => ({
    ...record,
    subject: record[subjectKey] ? subjectMap.get(record[subjectKey]) || { id: record[subjectKey], name: "Unknown subject" } : null
  }));
};

const normalizeContentArray = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => `${entry}`.trim()).filter(Boolean);
  }

  const trimmed = `${value || ""}`.trim();
  return trimmed ? [trimmed] : [];
};

const buildAtpItemPayload = (userId, itemData) => ({
  userId,
  atpId: itemData.atpId,
  subject: itemData.subject,
  grade: itemData.grade,
  topic: itemData.topic,
  content: normalizeContentArray(itemData.content),
  duration: itemData.duration || "",
  plannedStart: itemData.plannedStart,
  actualStart: itemData.actualStart || null,
  plannedFinish: itemData.plannedFinish,
  actualFinish: itemData.actualFinish || null,
  term: itemData.term,
  progress: itemData.progress || "Planned",
  status: itemData.status || "On Schedule",
  source: itemData.source || "manual",
  updatedAt: serverTimestamp()
});

// Annual Teaching Plan plans
export const fetchAnnualTeachingPlanPlans = async (userId) => {
  try {
    const plansRef = collection(db, ATP_PLANS_COLLECTION);
    const q = query(plansRef, where("userId", "==", userId), orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    const plans = querySnapshot.docs.map(mapDoc);
    const enrichedPlans = await enrichWithSubjects(plans);

    return enrichedPlans;
  } catch (error) {
    console.error("Error fetching ATP plans:", error);
    throw error;
  }
};

export const createAnnualTeachingPlanPlan = async (userId, planData) => {
  try {
    const payload = {
      userId,
      title: planData.title,
      subject: planData.subject,
      grade: planData.grade,
      academicYear: planData.academicYear,
      description: planData.description || "",
      source: planData.source || "manual",
      itemCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, ATP_PLANS_COLLECTION), payload);
    return { id: docRef.id, ...payload };
  } catch (error) {
    console.error("Error creating ATP plan:", error);
    throw error;
  }
};

export const updateAnnualTeachingPlanPlan = async (planId, planData) => {
  try {
    const payload = {
      title: planData.title,
      subject: planData.subject,
      grade: planData.grade,
      academicYear: planData.academicYear,
      description: planData.description || "",
      updatedAt: serverTimestamp()
    };

    await updateDoc(doc(db, ATP_PLANS_COLLECTION, planId), payload);
    return { id: planId, ...payload };
  } catch (error) {
    console.error("Error updating ATP plan:", error);
    throw error;
  }
};

export const deleteAnnualTeachingPlanPlan = async (planId) => {
  try {
    const itemsQuery = query(collection(db, ATP_ITEMS_COLLECTION), where("atpId", "==", planId));
    const itemsSnapshot = await getDocs(itemsQuery);
    const batch = writeBatch(db);

    itemsSnapshot.forEach((itemDoc) => {
      batch.delete(itemDoc.ref);
    });
    batch.delete(doc(db, ATP_PLANS_COLLECTION, planId));

    await batch.commit();
    return { success: true };
  } catch (error) {
    console.error("Error deleting ATP plan:", error);
    throw error;
  }
};

// Annual Teaching Plan items
export const fetchAnnualTeachingPlans = async (userId, options = {}) => {
  try {
    const constraints = [where("userId", "==", userId), orderBy("createdAt", "desc")];

    if (options.planId) {
      constraints.unshift(where("atpId", "==", options.planId));
    }

    const q = query(collection(db, ATP_ITEMS_COLLECTION), ...constraints);
    const querySnapshot = await getDocs(q);
    const atpItems = querySnapshot.docs.map(mapDoc);
    const enrichedItems = await enrichWithSubjects(atpItems);

    return enrichedItems;
  } catch (error) {
    console.error("Error fetching ATP items:", error);
    throw error;
  }
};

const syncAtpPlanItemCount = async (planId) => {
  if (!planId) {
    return;
  }

  const itemsQuery = query(collection(db, ATP_ITEMS_COLLECTION), where("atpId", "==", planId));
  const itemsSnapshot = await getDocs(itemsQuery);
  await updateDoc(doc(db, ATP_PLANS_COLLECTION, planId), {
    itemCount: itemsSnapshot.size,
    updatedAt: serverTimestamp()
  });
};

export const createAnnualTeachingPlan = async (userId, itemData) => {
  try {
    const item = {
      ...buildAtpItemPayload(userId, itemData),
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, ATP_ITEMS_COLLECTION), item);
    await syncAtpPlanItemCount(itemData.atpId);

    return {
      id: docRef.id,
      ...item
    };
  } catch (error) {
    console.error("Error creating ATP item:", error);
    throw error;
  }
};

export const createAnnualTeachingPlansBulk = async (userId, planId, items = []) => {
  try {
    const batch = writeBatch(db);

    items.forEach((itemData) => {
      const docRef = doc(collection(db, ATP_ITEMS_COLLECTION));
      batch.set(docRef, {
        ...buildAtpItemPayload(userId, { ...itemData, atpId: planId }),
        createdAt: serverTimestamp()
      });
    });

    await batch.commit();
    await syncAtpPlanItemCount(planId);

    return { success: true, count: items.length };
  } catch (error) {
    console.error("Error importing ATP items:", error);
    throw error;
  }
};

export const updateAnnualTeachingPlan = async (itemId, itemData) => {
  try {
    const itemRef = doc(db, ATP_ITEMS_COLLECTION, itemId);
    const currentSnapshot = await getDoc(itemRef);
    const previousPlanId = currentSnapshot.exists() ? currentSnapshot.data().atpId : null;
    const updateData = buildAtpItemPayload(itemData.userId, itemData);

    await updateDoc(itemRef, updateData);
    if (previousPlanId && previousPlanId !== itemData.atpId) {
      await syncAtpPlanItemCount(previousPlanId);
    }
    await syncAtpPlanItemCount(itemData.atpId);

    return {
      id: itemId,
      ...updateData
    };
  } catch (error) {
    console.error("Error updating ATP item:", error);
    throw error;
  }
};

export const deleteAnnualTeachingPlan = async (itemId, planId = null) => {
  try {
    await deleteDoc(doc(db, ATP_ITEMS_COLLECTION, itemId));
    await syncAtpPlanItemCount(planId);
    return { success: true };
  } catch (error) {
    console.error("Error deleting ATP item:", error);
    throw error;
  }
};

// Programme of Assessment
export const fetchProgrammeOfAssessment = async (userId) => {
  try {
    const poaRef = collection(db, POA_COLLECTION);
    const q = query(poaRef, where("userId", "==", userId), orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    const tasks = querySnapshot.docs.map(mapDoc);

    return await enrichWithSubjects(tasks);
  } catch (error) {
    console.error("Error fetching POA tasks:", error);
    throw error;
  }
};

export const createProgrammeOfAssessmentTask = async (userId, taskData) => {
  try {
    const task = {
      userId,
      title: taskData.title,
      plannedDate: taskData.plannedDate,
      term: taskData.term,
      subject: taskData.subject,
      grade: taskData.grade,
      topics: taskData.topics,
      category: taskData.category,
      taskType: taskData.taskType,
      marks: taskData.marks,
      weighting: taskData.weighting,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, POA_COLLECTION), task);

    return {
      id: docRef.id,
      ...task
    };
  } catch (error) {
    console.error("Error creating POA task:", error);
    throw error;
  }
};

export const updateProgrammeOfAssessmentTask = async (taskId, taskData) => {
  try {
    const taskRef = doc(db, POA_COLLECTION, taskId);
    const updateData = {
      title: taskData.title,
      plannedDate: taskData.plannedDate,
      term: taskData.term,
      subject: taskData.subject,
      grade: taskData.grade,
      topics: taskData.topics,
      category: taskData.category,
      taskType: taskData.taskType,
      marks: taskData.marks,
      weighting: taskData.weighting,
      updatedAt: serverTimestamp()
    };

    await updateDoc(taskRef, updateData);

    return {
      id: taskId,
      ...updateData
    };
  } catch (error) {
    console.error("Error updating POA task:", error);
    throw error;
  }
};

export const deleteProgrammeOfAssessmentTask = async (taskId) => {
  try {
    await deleteDoc(doc(db, POA_COLLECTION, taskId));
    return { success: true };
  } catch (error) {
    console.error("Error deleting POA task:", error);
    throw error;
  }
};

// Task Marks
export const fetchTaskMarks = async (taskId) => {
  try {
    const marksRef = collection(db, POA_COLLECTION, taskId, "marks");
    const querySnapshot = await getDocs(marksRef);

    return querySnapshot.docs.map(mapDoc);
  } catch (error) {
    console.error("Error fetching task marks:", error);
    throw error;
  }
};

export const saveTaskMarks = async (taskId, learnerId, markData) => {
  try {
    const markRef = doc(db, POA_COLLECTION, taskId, "marks", learnerId);

    await setDoc(markRef, {
      ...markData,
      updatedAt: serverTimestamp()
    });

    return { success: true, id: learnerId };
  } catch (error) {
    console.error("Error saving task mark:", error);
    throw error;
  }
};

// Subjects (reused from other services)
export const fetchSubjects = async (userId) => {
  try {
    const subjectsRef = collection(db, "subjects");
    const q = query(subjectsRef, where("userId", "==", userId), orderBy("name"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(mapDoc);
  } catch (error) {
    console.error("Error fetching subjects:", error);
    return generateMockSubjects();
  }
};

// Learners
export const fetchLearners = async (grade) => {
  try {
    if (!grade) return [];

    const learnersRef = collection(db, "learners");
    const q = query(learnersRef, where("grade", "==", grade), orderBy("lastName"), orderBy("firstName"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((snapshot) => {
      const learner = snapshot.data();
      return {
        id: snapshot.id,
        name: `${learner.firstName} ${learner.lastName}`,
        ...learner
      };
    });
  } catch (error) {
    console.error("Error fetching learners:", error);
    return generateMockLearners(grade);
  }
};

function generateMockSubjects() {
  return [
    { id: "subj1", name: "Mathematics", grade: "10", department: "Mathematics" },
    { id: "subj2", name: "Physical Sciences", grade: "10", department: "Sciences" },
    { id: "subj3", name: "Life Sciences", grade: "11", department: "Sciences" },
    { id: "subj4", name: "English Home Language", grade: "9", department: "Languages" },
    { id: "subj5", name: "Geography", grade: "8", department: "Social Sciences" }
  ];
}

function generateMockLearners(grade) {
  const firstNames = [
    "Emma", "Liam", "Olivia", "Noah", "Ava", "Ethan", "Sofia", "Lucas",
    "Isabella", "Mason", "Mia", "Oliver", "Zoe", "Jacob", "Lily", "Logan",
    "Chloe", "Aiden", "Madison", "Jackson", "Emily", "Amelia", "Sipho",
    "Thabo", "Lerato", "Nandi", "Thabiso", "Phumzile", "Neo", "Lesedi"
  ];

  const lastNames = [
    "Smith", "Johnson", "Williams", "Brown", "Jones", "Garcia", "Miller",
    "Davis", "Rodriguez", "Martinez", "Hernandez", "Lopez", "Gonzalez",
    "Wilson", "Anderson", "Thomas", "Taylor", "Moore", "Jackson", "Martin",
    "Dlamini", "Nkosi", "Ndlovu", "Khumalo", "Mokoena", "Molefe", "Mkhize",
    "Mbatha", "Sithole", "Mabaso"
  ];

  const learnerCount = Math.floor(Math.random() * 11) + 20;
  const learners = [];

  for (let i = 0; i < learnerCount; i += 1) {
    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];

    learners.push({
      id: `learner-${i + 1}`,
      firstName,
      lastName,
      name: `${firstName} ${lastName}`,
      grade
    });
  }

  return learners;
}
